// apps/frontend/src/components/ui/Toast.tsx
// 🎨 Toast PREMIUM - Design Mode App avec couleurs harmonieuses

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

interface ToastProps {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  translationKey?: string;
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({
  type,
  message,
  translationKey,
  duration = 4000,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const [isExiting, setIsExiting] = useState(false);

  const displayMessage = translationKey ? t(translationKey) : message;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 400);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getToastConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle className="h-6 w-6 text-brand-500" />,
          bgGradient: 'linear-gradient(135deg, #FDFCFA 0%, #F8F5F0 100%)',
          borderColor: 'var(--brand-500)',
          textColor: '#5D4E37',
          shadowColor: 'rgba(200, 168, 130, 0.25)',
        };
      case 'error':
        return {
          icon: <XCircle className="h-6 w-6" style={{ color: '#B8956A' }} />,
          bgGradient: 'linear-gradient(135deg, #FDFCFA 0%, #FAF7F2 100%)',
          borderColor: '#A67C52',
          textColor: '#8B4513',
          shadowColor: 'rgba(166, 124, 82, 0.25)',
        };
      case 'warning':
        return {
          icon: <AlertTriangle className="h-6 w-6 text-brand-500" />,
          bgGradient: 'linear-gradient(135deg, #FDFCFA 0%, #F8F5F0 100%)',
          borderColor: 'var(--brand-500)',
          textColor: '#5D4E37',
          shadowColor: 'rgba(200, 168, 130, 0.25)',
        };
      case 'info':
        return {
          icon: <Info className="h-6 w-6" style={{ color: '#9B8A6B' }} />,
          bgGradient: 'linear-gradient(135deg, #FDFCFA 0%, #F8F5F0 100%)',
          borderColor: '#9B8A6B',
          textColor: '#5D4E37',
          shadowColor: 'rgba(155, 138, 107, 0.25)',
        };
    }
  };

  const config = getToastConfig();

  return (
    <div
      style={{
        position: 'fixed',
        top: '24px',
        left: '50%',
        transform: isExiting
          ? 'translateX(-50%) translateY(-20px) scale(0.95)'
          : 'translateX(-50%) translateY(0) scale(1)',
        zIndex: 9999,
        width: '90%',
        maxWidth: '420px',
        opacity: isExiting ? 0 : 1,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div
        style={{
          background: config.bgGradient,
          border: `2px solid ${config.borderColor}`,
          borderRadius: '12px',
          boxShadow: `0 20px 25px -5px ${config.shadowColor}, 0 10px 10px -5px ${config.shadowColor}`,
          backdropFilter: 'blur(10px)',
          padding: '16px 20px',
        }}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-0.5">{config.icon}</div>
          <div className="ml-4 flex-1">
            <p
              style={{
                color: config.textColor,
                fontSize: '15px',
                fontWeight: '500',
                lineHeight: '1.4',
                margin: 0,
              }}
            >
              {displayMessage}
            </p>
          </div>
          <button
            onClick={() => {
              setIsExiting(true);
              setTimeout(onClose, 400);
            }}
            style={{
              marginLeft: '16px',
              color: '#9CA3AF',
              transition: 'color 0.2s ease',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = config.borderColor;
              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = '#9CA3AF';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
